import React, { useCallback, useMemo } from "react";

import { useIntl } from "react-intl";

import ActivityStatusOption from "@mapmycustomers/shared/enum/activity/ActivityStatusOption";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import { Activity } from "@mapmycustomers/shared/types/entity";
import FilterModel from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";

import SelectField from "@app/component/input/SelectField";
import useStatus from "@app/component/QuickFilterBar/utils/useStatus";
import useView from "@app/component/view/utils/useView";
import { smartPlannerViewAnalytics } from "@app/scene/activity/activityTableAnalytics";
import { getActivityStatusOptionDisplayName } from "@app/util/ui";

const allStatusOptions = [
  ActivityStatusOption.ALL,
  ActivityStatusOption.COMPLETED,
  ActivityStatusOption.INCOMPLETE,
];

const statusFilterValues: Partial<Record<ActivityStatusOption, Partial<FilterModel>>> = {
  [ActivityStatusOption.ALL]: { completed: undefined },
  [ActivityStatusOption.COMPLETED]: { completed: { operator: FilterOperator.EQUALS, value: true } },
  [ActivityStatusOption.INCOMPLETE]: {
    completed: { operator: FilterOperator.EQUALS, value: false },
  },
};

interface Props {}

const QuickStatusFilter: React.FC<Props> = () => {
  const intl = useIntl();
  const {
    updateFilterModel,
    viewState: { filter },
  } = useView<Activity>();

  const isCompleted = useStatus(statusFilterValues[ActivityStatusOption.COMPLETED]!, filter);
  const isIncomplete = useStatus(statusFilterValues[ActivityStatusOption.INCOMPLETE]!, filter);
  const currentValue = isCompleted
    ? ActivityStatusOption.COMPLETED
    : isIncomplete
    ? ActivityStatusOption.INCOMPLETE
    : ActivityStatusOption.ALL;

  const handleChange = useCallback(
    (option: ActivityStatusOption) => {
      updateFilterModel(statusFilterValues[option]!);
    },
    [updateFilterModel]
  );

  const options = useMemo(
    () =>
      allStatusOptions.map((value) => ({
        label: getActivityStatusOptionDisplayName(intl, value),
        value,
      })),
    [intl]
  );

  const handleClick = useCallback(() => {
    smartPlannerViewAnalytics.clicked(["Activity Status Dropdown"]);
  }, []);

  return (
    <SelectField
      dropdownMatchSelectWidth={false}
      onChange={handleChange}
      onClick={handleClick}
      options={options}
      value={currentValue}
    />
  );
};

export default QuickStatusFilter;
